import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { RefreshCw, Activity, ArrowDownLeft, ArrowUpRight, AlertTriangle, Clock } from 'lucide-react';
import { getEdiMonitoring } from '../../config/api';

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-lg shadow p-5">
    <div className="flex items-center">
      <Icon className={`h-7 w-7 ${tone === 'red' ? 'text-red-500' : tone === 'blue' ? 'text-blue-500' : tone === 'purple' ? 'text-purple-500' : 'text-gray-500'}`} />
      <div className="ml-3"><p className="text-xs font-medium text-gray-500">{label}</p><p className="text-2xl font-semibold text-gray-900">{value}</p></div>
    </div>
  </div>
);

const EDIMonitoring = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(true);

  const load = useCallback(async () => {
    try { setData(await getEdiMonitoring()); }
    catch (e) { toast.error('Failed to load EDI monitoring'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(load, 15000); // live refresh
    return () => clearInterval(id);
  }, [auto, load]);

  if (loading || !data) return <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">Loading monitoring…</div>;

  const types = ['844', '845', '849', 'EXCEL', 'MANUAL'].filter(t => data.byType?.[t]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className={`inline-flex h-2.5 w-2.5 rounded-full ${auto ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
          {auto ? 'Live — refreshing every 15s' : 'Paused'}
          {data.lastActivityAt && <span className="text-gray-400">· last activity {new Date(data.lastActivityAt).toLocaleString()}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAuto(a => !a)} title={auto ? 'Pause' : 'Resume'} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">{auto ? 'Pause' : 'Resume'}</button>
          <button onClick={load} title="Refresh" className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Stat icon={Activity} label="Total transactions" value={data.total ?? 0} />
        <Stat icon={ArrowDownLeft} label="Inbound" value={data.byDirection?.Inbound ?? 0} tone="blue" />
        <Stat icon={ArrowUpRight} label="Outbound" value={data.byDirection?.Outbound ?? 0} tone="purple" />
        <Stat icon={AlertTriangle} label="Errors" value={data.errorTotal ?? 0} tone={data.errorTotal ? 'red' : undefined} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">By transaction type</h3>
          <div className="space-y-3">
            {types.length === 0 ? <p className="text-sm text-gray-500">No transactions yet.</p> : types.map(t => {
              const n = data.byType[t]; const pct = data.total ? Math.round((n / data.total) * 100) : 0;
              return (
                <div key={t}>
                  <div className="flex justify-between text-sm mb-1"><span className="font-medium text-gray-700">EDI {t}</span><span className="text-gray-500">{n} ({pct}%)</span></div>
                  <div className="w-full bg-gray-100 rounded-full h-2"><div className="bg-primary-500 h-2 rounded-full" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2"><Clock className="h-5 w-5 text-gray-500" /> Recent activity</h3>
          <div className="space-y-3">
            {(data.recent || []).length === 0 ? <p className="text-sm text-gray-500">No recent activity.</p> : data.recent.map(t => (
              <div key={t.id} className="flex items-center justify-between border-b border-gray-50 pb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {t.direction === 'Inbound' ? <ArrowDownLeft className="h-4 w-4 text-blue-500 flex-shrink-0" /> : <ArrowUpRight className="h-4 w-4 text-purple-500 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 truncate">EDI {t.docType} · {t.kind}</p>
                    <p className="text-xs text-gray-400 truncate">{t.reference}</p>
                  </div>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{t.at ? new Date(t.at).toLocaleTimeString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EDIMonitoring;
